import { Step as StepProps } from '@service-victoria/component-library/components/Wizard/props';
import { FormikErrors, FormikTouched } from 'formik';
import { lazy } from 'react';
import * as Yup from 'yup';

import * as Interfaces from 'app/interfaces';
import * as Steps from '../..';
import { constants, validations } from 'app/utils';

import { validateOrRedeemIndividualVoucher } from '../../../../../services/gakService';
import type { FormValues } from './interfaces';

const invalidAmount =
  'Please enter a value between $0 and $200, with no more than 2 decimals places.';

const initialValues: FormValues = {
  redeem: {
    individual: {
      voucherCode: '',
      childName: '',
      activityType: '',
      amount: ''
    }
  }
};

const initialErrors: FormikErrors<FormValues> = {};
const initialTouched: FormikTouched<FormValues> = {};

export const individualSchema = Yup.object({
  voucherCode: Yup.string().required(constants.GenericValidationErrors.required),
  childName: Yup.string().default(undefined),
  activityType: Yup.string().required(constants.GenericValidationErrors.required),
  amount: Yup.string()
    .required(constants.GenericValidationErrors.required)
    .test('isCurrency', invalidAmount, function validate(amount = '') {
      return validations.isValidCurrency(amount);
    })
    .test('inValidRange', invalidAmount, function validate(amount = '') {
      const number = Number(amount.replace('$', ''));
      return number > 0 && number <= 200;
    })
});

const redeemSchema = Yup.object<FormValues['redeem']>().shape({
  individual: individualSchema
});

const validationSchema = Yup.object<FormValues>().shape({
  redeem: redeemSchema
});

const Component = lazy(() => import('./RedeemIndividual'));

const Step: StepProps<FormValues> = {
  id: Interfaces.Common.RedeemStep.Individual,
  title: constants.RedemptionPage.title,
  stepName: 'Redeem voucher',
  Component,
  validation: {
    schema: validationSchema,
    fieldNames: [
      'redeem.individual.voucherCode',
      'redeem.individual.childName',
      'redeem.individual.activityType',
      'redeem.individual.amount'
    ]
  },
  navigation: {
    normal: [
      {
        content: 'Back',
        type: 'secondary',
        shouldValidate: false,
        onClick: () => {
          window.location.href = `${import.meta.env.BASE_URL}${constants.Routes.Dashboard}` || '/';
        }
      },
      {
        content: 'Check voucher',
        shouldValidate: true,
        type: 'secondary',
        onClick: async (context, formProps, appStates) => {
          context.dispatch({
            type: 'API_REQUEST',
            status: 'pending'
          });

          const validateOnly = true;
          await validateOrRedeemIndividualVoucher(
            formProps.values.redeem.individual,
            formProps,
            appStates,
            validateOnly
          );

          context.dispatch({
            type: 'API_REQUEST',
            status: 'resolved'
          });
        }
      },
      {
        content: 'Check and redeem',
        shouldValidate: true,
        type: 'primary',
        onClick: async (context, formProps, appStates): Promise<void> => {
          context.dispatch({
            type: 'API_REQUEST',
            status: 'pending'
          });

          const validateOnly = true;
          const { success, orgName } = await validateOrRedeemIndividualVoucher(
            formProps.values.redeem.individual,
            formProps,
            appStates,
            validateOnly
          );

          if (success) {
            const formValues = {
              ...formProps.values,
              redeem: {
                ...formProps.values.redeem,
                individual: {
                  ...formProps.values.redeem.individual,
                  isValid: success,
                  providerName: orgName
                }
              }
            };

            context.dispatch({
              type: 'GOTO_STEP',
              stepId: Steps.Review.Step.id,
              values: formValues
            });
          }

          // stop spinner
          context.dispatch({
            type: 'API_REQUEST',
            status: 'resolved'
          });
        }
      }
    ]
  }
};

export { Step, initialErrors, initialTouched, initialValues, redeemSchema, validationSchema };
